import { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { Typography, Grid, Switch, FormControlLabel, Box, Button } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbarCustom from "examples/Navbars/DashboardNavbarCustom";
import SoftBox from "components/SoftBox";
import Breadcrumb from "components/Breadcrumb";
// import DragDropZone from "components/DragDropZone";
import CustomTextField from "components/TextField";
import CustomSelect from "components/CustomSelect";
import ShirtViewerPreview from "components/ShirtViewerPreview/ShirtViewerPreview";
import { fetchTemplateCreate, fetchTemplateUpdate } from "api/templateService";
import { getImageUrl } from "api/AttachmentAPI";
import { fetchCategoryOptions } from "api/categoryService";
import LayerTable from "./LayerTable";
import useFetchLayers from "../../../components/useFetchLayers/useFetchLayers";

function AddDetailTemplate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedCategory, templateDetail } = location.state || {};
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [formData, setFormData] = useState({
    categoryId: templateDetail?.categoryId || selectedCategory?.categoryId || null,
    templateName: templateDetail?.templateName || "",
    displayOrder: templateDetail?.displayOrder || 1,
    status: templateDetail ? templateDetail.status === 1 : true,
    images: {
      front: null,
      back: [],
      preview3D: null,
      manocanh: [],
    },
  });
  const isEditMode = Boolean(templateDetail?.templateId);

  const [layers, setLayers] = useState([]);
  const [layersFront, setLayersFront] = useState([]);
  const [layersBack, setLayersBack] = useState([]);
  const [layersManocanh, setLayersManocanh] = useState([]);

  useEffect(() => {
    setLayersFront(layers.filter((l) => l.layerType === "FRONT" && !l.isDelete));
    setLayersBack(layers.filter((l) => l.layerType === "BACK" && !l.isDelete));
    setLayersManocanh(layers.filter((l) => l.layerType === "MANOCANH" && !l.isDelete));
  }, [layers]);

  const { fetchLayersByTemplateId } = useFetchLayers();

  const frontRef = useRef();
  const backRef = useRef();
  const manocanhRef = useRef();

  useEffect(() => {
    const loadLayers = async () => {
      if (!templateDetail?.templateId) return;

      await fetchLayersByTemplateId(templateDetail.templateId, async (data) => {
        const mapped = await Promise.all(
          data.map(async (l) => {
            const imageUrl = l.attachment?.encryptedId
              ? await getImageUrl(`/api/attachments/downloadFile/${l.attachment.encryptedId}`)
              : null;

            return {
              ...l,
              layerType: l.layerType === 1 ? "FRONT" : l.layerType === 2 ? "BACK" : "MANOCANH",
              imageUrl,
            };
          })
        );
        setLayers(mapped);
      });
    };

    loadLayers();
  }, [templateDetail, fetchLayersByTemplateId]);

  useEffect(() => {
    const preloadImages = async () => {
      if (!templateDetail?.attachments) return;

      const tasks = templateDetail.attachments.map(async (att, index) => {
        const imageUrl = await getImageUrl(`/api/attachments/downloadFile/${att.encryptedId}`);
        return {
          type: att.attachType,
          url: imageUrl,
          encryptedId: att.encryptedId,
          index,
        };
      });

      const results = await Promise.all(tasks);

      const layersFromAttachments = results.map(({ type, url, encryptedId, index }) => ({
        layerId: `${type}_${encryptedId}`,
        layerName: `Layer ${index + 1}`,
        layerCode: `${index + 1}`,
        createTime: new Date().toISOString().split("T")[0],
        isColorChange: true,
        layerType: type === 1 ? "FRONT" : type === 2 ? "BACK" : type === 4 ? "MANOCANH" : "UNKNOWN",
        imageUrl: url,
        attachment: { encryptedId },
        isTemp: false,
      }));

      setLayers(layersFromAttachments);

      // Optional: set formData.images nếu bạn vẫn cần preview riêng
      const newImages = {
        front: results.find((r) => r.type === 1)?.url || null,
        back: results.filter((r) => r.type === 2).map((r) => ({ imageUrl: r.url })),
        preview3D: results.find((r) => r.type === 3)?.url || null,
        manocanh: results.filter((r) => r.type === 4).map((r) => ({ imageUrl: r.url })),
      };

      setFormData((prev) => ({ ...prev, images: newImages }));
    };

    preloadImages();
  }, [templateDetail]);

  useEffect(() => {
    const loadOptions = async () => {
      const options = await fetchCategoryOptions();
      setCategoryOptions(options);

      // Nếu là thêm mới thì gán mặc định từ selectedCategory
      if (!templateDetail && selectedCategory?.categoryId) {
        setFormData((prev) => ({ ...prev, categoryId: selectedCategory.categoryId }));
      }
    };
    loadOptions();
  }, []);

  const [errors, setErrors] = useState({
    categoryId: "",
    templateName: "",
    displayOrder: "",
    imageFront: "",
  });

  const validateForm = () => {
    const newErrors = {
      categoryId: formData.categoryId ? "" : "Vui lòng chọn danh mục sản phẩm",
      templateName: formData.templateName.trim() ? "" : "Tên sản phẩm là bắt buộc",
      displayOrder: formData.displayOrder >= 1 ? "" : "Số thứ tự phải >= 1",
      imageFront: layers.length > 0 ? "" : "Hình ảnh mặt trước là bắt buộc",
    };

    setErrors(newErrors);
    return Object.values(newErrors).every((err) => err === "");
  };

  const convertStageToFile = async (stageRef, filename) => {
    if (!stageRef?.current) return null;
    const uri = stageRef.current.toDataURL({ pixelRatio: 2 });
    const res = await fetch(uri);
    const blob = await res.blob();
    return new File([blob], filename, { type: blob.type });
  };

  const getLayerTypeCode = (type) => {
    switch (type) {
      case "FRONT":
        return 1;
      case "BACK":
        return 2;
      case "MANOCANH":
        return 3;
      default:
        return 1; // mặc định là mặt trước
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const form = new FormData();
      form.append("categoryId", formData.categoryId);
      form.append("templateName", formData.templateName);
      form.append("displayOrder", formData.displayOrder);
      form.append("status", formData.status ? 1 : 0);

      // Convert từ ShirtViewer
      const frontFile = await convertStageToFile(frontRef, "imageFront.jpg");
      if (frontFile) form.append("imageFront", frontFile);

      const backFile = await convertStageToFile(backRef, "imageBack.jpg");
      if (backFile) form.append("imageBack", backFile);

      const manocanhFile = await convertStageToFile(manocanhRef, "imageManocanh.jpg");
      if (manocanhFile) form.append("imageManocanh", manocanhFile);

      // Gửi danh sách layer
      const deletedLayerIds = layers
        .filter((l) => l.isDelete && l.layerId && !l.isTemp)
        .map((l) => l.layerId);

      layers
        .filter((layer) => !layer.isDelete)
        .sort((a, b) => Number(a.display_order || 0) - Number(b.display_order || 0))
        .forEach((layer, index) => {
          if (/^\d+$/.test(layer.layerId)) {
            form.append(`layers[${index}].layerId`, layer.layerId);
          }
          form.append(`layers[${index}].displayOrder`, layer.display_order || index + 1);
          form.append(`layers[${index}].layerCode`, layer.layerCode || ""); // vẫn gửi layerCode
          form.append(`layers[${index}].layerName`, layer.layerName || "");
          form.append(`layers[${index}].layerType`, getLayerTypeCode(layer.layerType));
          form.append(`layers[${index}].createTime`, layer.createTime || "");
          form.append(`layers[${index}].isColorChange`, layer.isColorChange ? 1 : 0);
          form.append("deletedLayerIds", JSON.stringify(deletedLayerIds));

          if (layer.isTemp && layer.image) {
            form.append(`layers[${index}].image`, layer.image);
          }
        });

      let res;
      if (templateDetail?.templateId) {
        form.append("templateId", templateDetail.templateId);
        res = await fetchTemplateUpdate(templateDetail.templateId, form);
      } else {
        res = await fetchTemplateCreate(form);
      }

      if (res.success) {
        Swal.fire({
          icon: "success",
          title: "Thành công",
          text: templateDetail ? "Cập nhật thành công!" : "Tạo sản phẩm thành công!",
          timer: 2000,
          showConfirmButton: false,
        });
        navigate(-1);
      } else {
        Swal.fire({
          icon: "error",
          title: "Lỗi",
          text: res.message?.message || "Không rõ nguyên nhân.",
        });
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Lỗi khi gửi sản phẩm:", err);
      Swal.fire({
        icon: "error",
        title: "Lỗi hệ thống",
        text: "Có lỗi xảy ra khi gửi dữ liệu.",
      });
    }
  };

  const handleAddImagesUnified = (event, type) => {
    const files = Array.from(event.target.files || []);
    // const filtered = layers.filter((l) => l.layerType === type);
    // const nextIndex = filtered.length;

    const newLayers = files.map((file, index) => {
      const id = `${type}_${Date.now()}_${index}`;
      const name = file.name.replace(/\.[^/.]+$/, "");

      return {
        layerId: id,
        layerName: name,
        createTime: new Date().toISOString().split("T")[0],
        isColorChange: true,
        layerType: type,
        image: file,
        imageUrl: URL.createObjectURL(file),
        isTemp: true,
        layerCode: "", // để trống để xử lý sau
      };
    });

    // Cập nhật layers mới
    setLayers((prev) => {
      const updated = [...prev, ...newLayers];

      const regrouped = ["FRONT", "BACK", "MANOCANH"].flatMap((layerTypeKey) =>
        updated
          .filter((l) => l.layerType === layerTypeKey)
          .map((l, i) => ({
            ...l,
            layerCode: l.layerCode ? l.layerCode : `${i + 1}`, // chỉ sinh nếu rỗng
          }))
      );

      return regrouped;
    });

    event.target.value = null;
  };

  const handleDragEndByType = (type) => (result) => {
    if (!result.destination) return;

    setLayers((prevLayers) => {
      const movedLayers = prevLayers.filter((l) => l.layerType === type && !l.isDelete);
      const otherLayers = prevLayers.filter((l) => l.layerType !== type || l.isDelete);

      const [movedItem] = movedLayers.splice(result.source.index, 1);
      movedLayers.splice(result.destination.index, 0, movedItem);

      const updatedMovedLayers = movedLayers.map((l, idx) => ({
        ...l,
        display_order: idx + 1,
        layerCode: l.layerCode || `${idx + 1}`,
      }));

      // Giữ nguyên thứ tự gốc của otherLayers
      const finalList = [...otherLayers, ...updatedMovedLayers];

      // Đảm bảo không bị mất thứ tự: sort lại theo thời gian thêm
      finalList.sort((a, b) => {
        const orderA = a.display_order || 0;
        const orderB = b.display_order || 0;
        return orderA - orderB;
      });

      return finalList;
    });
  };

  // const handleExportFront = () => {
  //   if (!frontRef?.current) return;
  //   const uri = frontRef.current.toDataURL({ pixelRatio: 2 });
  //   const link = document.createElement("a");
  //   link.href = uri;
  //   link.download = `preview_front_${Date.now()}.png`;
  //   document.body.appendChild(link);
  //   link.click();
  //   document.body.removeChild(link);
  // };

  // const handleExportBack = () => {
  //   if (!backRef?.current) return;
  //   const uri = backRef.current.toDataURL({ pixelRatio: 2 });
  //   const link = document.createElement("a");
  //   link.href = uri;
  //   link.download = `preview_back_${Date.now()}.png`;
  //   link.click();
  // };

  // const handleExportManocanh = () => {
  //   if (!manocanhRef?.current) return;
  //   const uri = manocanhRef.current.toDataURL({ pixelRatio: 2 });
  //   const link = document.createElement("a");
  //   link.href = uri;
  //   link.download = `preview_mano_${Date.now()}.png`;
  //   link.click();
  // };

  return (
    <DashboardLayout>
      <DashboardNavbarCustom
        page="Quản lý danh mục"
        childPage={["Chi tiết danh mục", isEditMode ? "Chỉnh sửa sản phẩm" : "Thêm mới sản phẩm"]}
      />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Grid container>
            <Grid item xs={0.2} sm={0.2} xl={0.2} />
            <Grid item xs={11.6} sm={11.6} xl={11.6}>
              <Breadcrumb
                items={[
                  { label: "Quản lý danh mục", onClick: () => navigate("/Category") },
                  { label: "Chi tiết danh mục", onClick: () => navigate(-1) },
                  isEditMode ? "Chỉnh sửa sản phẩm" : "Thêm mới sản phẩm",
                ]}
              />

              {/* NÚT LƯU */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
                <Typography fontSize="24px" fontWeight="bold">
                  {isEditMode ? "CHỈNH SỬA SẢN PHẨM" : "THÊM MỚI SẢN PHẨM"}
                </Typography>

                <Button variant="contained" color="primary" onClick={handleSubmit}>
                  {isEditMode ? "Cập nhật" : "Tạo mới"}
                </Button>
              </Box>

              {/* FORM NHẬP TEMPLATE */}
              <Grid container rowSpacing={0} columnSpacing={2} sx={{ my: 4 }}>
                <Grid item xs={12} md={6}>
                  <CustomSelect
                    label="Chọn danh mục sản phẩm"
                    value={formData.categoryId}
                    required
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, categoryId: e.target.value }))
                    }
                    options={categoryOptions}
                    placeholder="-- Chọn danh mục --"
                    error={Boolean(errors.categoryId)}
                    helperText={errors.categoryId}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Tên sản phẩm"
                    type="text"
                    value={formData.templateName}
                    required
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, templateName: e.target.value }))
                    }
                    error={Boolean(errors.templateName)}
                    helperText={errors.templateName}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CustomTextField
                    label="Số thứ tự hiển thị"
                    type="number"
                    value={formData.displayOrder}
                    required
                    onChange={(e) =>
                      setFormData((prev) => ({ ...prev, displayOrder: Number(e.target.value) }))
                    }
                    error={Boolean(errors.displayOrder)}
                    helperText={errors.displayOrder}
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  <FormControlLabel
                    sx={{ ml: 0 }}
                    control={
                      <Switch
                        checked={formData.status}
                        onChange={(e) =>
                          setFormData((prev) => ({ ...prev, status: e.target.checked }))
                        }
                      />
                    }
                    label="Kích hoạt"
                  />
                </Grid>
              </Grid>

              {/* Mặt trước */}
              <hr />
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
                <Typography
                  fontSize="24px"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ my: 2, mt: 4 }}
                  color="text.primary"
                >
                  MẶT TRƯỚC
                </Typography>
                <Button variant="contained" color="primary" component="label">
                  Thêm mới layer
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => handleAddImagesUnified(e, "FRONT")}
                  />
                </Button>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                {/* Left - Layer Table */}
                <Grid item xs={12} md={9}>
                  <LayerTable
                    label="Danh sách layer"
                    droppableId="droppable-front"
                    layers={layersFront}
                    setLayers={setLayers}
                    onEdit={(id, key, value) =>
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, [key]: value } : l))
                      )
                    }
                    onDragEnd={handleDragEndByType("FRONT")}
                    onDelete={(id) =>
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, isDelete: true } : l))
                      )
                    }
                  />
                </Grid>
                {/* Right - Preview */}
                <Grid item xs={12} md={3}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    height="100%"
                  >
                    <ShirtViewerPreview
                      width={300}
                      height={320}
                      layers={layersFront}
                      title="Hình ảnh preview"
                      stageRef={frontRef}
                      zoom={1}
                      // topRightSlot={
                      //   <Button variant="outlined" size="small" onClick={handleExportFront}>
                      //     Xuất ảnh
                      //   </Button>
                      // }
                    />
                  </Box>
                </Grid>
              </Grid>

              {/* Mặt sau */}
              <hr />
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
                <Typography fontSize="24px" fontWeight="bold" gutterBottom sx={{ my: 2, mt: 4 }}>
                  MẶT SAU
                </Typography>
                <Button variant="contained" color="primary" component="label">
                  Thêm mới layer
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => handleAddImagesUnified(e, "BACK")}
                  />
                </Button>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                <Grid item xs={12} md={9}>
                  <LayerTable
                    label="Danh sách ảnh mặt sau"
                    droppableId="droppable-back"
                    layers={layersBack}
                    setLayers={(newLayers) =>
                      setFormData((prev) => ({
                        ...prev,
                        images: { ...prev.images, back: newLayers },
                      }))
                    }
                    onEdit={(id, key, value) =>
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, [key]: value } : l))
                      )
                    }
                    onDelete={(id) => {
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, isDelete: true } : l))
                      );
                      setFormData((prev) => ({
                        ...prev,
                        images: {
                          ...prev.images,
                          back: prev.images.back.filter((l) => l.layerId !== id),
                        },
                      }));
                    }}
                    onDragEnd={handleDragEndByType("BACK")}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    height="100%"
                  >
                    <ShirtViewerPreview
                      width={300}
                      height={320}
                      layers={layersBack}
                      title="Hình ảnh preview"
                      zoom={1}
                      stageRef={backRef}
                    />
                  </Box>
                </Grid>
              </Grid>

              <hr />
              {/* Manocanh */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mt={2} mb={2}>
                <Typography fontSize="24px" fontWeight="bold" gutterBottom sx={{ my: 2, mt: 4 }}>
                  MANOCANH
                </Typography>
                <Button variant="contained" color="primary" component="label">
                  Thêm mới layer
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    hidden
                    onChange={(e) => handleAddImagesUnified(e, "MANOCANH")}
                  />
                </Button>
              </Box>
              <Grid container spacing={2} sx={{ mb: 3 }} alignItems="stretch">
                <Grid item xs={12} md={9}>
                  <LayerTable
                    label="Danh sách ảnh manocanh"
                    droppableId="droppable-mano"
                    layers={layersManocanh}
                    onDragEnd={handleDragEndByType("MANOCANH")}
                    setLayers={(newLayers) =>
                      setFormData((prev) => ({
                        ...prev,
                        images: { ...prev.images, manocanh: newLayers },
                      }))
                    }
                    onEdit={(id, key, value) =>
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, [key]: value } : l))
                      )
                    }
                    onDelete={(id) => {
                      setLayers((prev) =>
                        prev.map((l) => (l.layerId === id ? { ...l, isDelete: true } : l))
                      );
                      setFormData((prev) => ({
                        ...prev,
                        images: {
                          ...prev.images,
                          manocanh: prev.images.manocanh.filter((l) => l.layerId !== id),
                        },
                      }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-start"
                    alignItems="center"
                    height="100%"
                  >
                    <ShirtViewerPreview
                      width={300}
                      height={320}
                      layers={layersManocanh}
                      title="Hình ảnh preview"
                      zoom={1}
                      stageRef={manocanhRef}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={0.2} sm={0.2} xl={0.2} />
          </Grid>
        </SoftBox>
      </SoftBox>
    </DashboardLayout>
  );
}

export default AddDetailTemplate;
